/**
 * Offline-aware technician operations.
 * Strategy:
 *   - GET /schedules + /clients : try network, cache results; if offline use cache.
 *   - POST start/not-done/complete : if offline, enqueue + optimistic local update.
 */
import api, { ASSET_BASE } from "@/lib/api";
import {
    cacheSchedules, getCachedSchedules, getCachedSchedule, updateCachedSchedule,
    cacheClients, getCachedClients,
    cacheReports,
    enqueueOutbox,
    setMeta,
} from "@/lib/offlineStore";
import { drain } from "@/lib/syncEngine";

function isOnline() { return typeof navigator === "undefined" ? true : navigator.onLine; }
function nowIso() { return new Date().toISOString(); }
function randId() { return "local-" + Math.random().toString(36).slice(2, 10); }
function randPin() { return String(Math.floor(1000 + Math.random() * 9000)); }

/**
 * Load technician's route for a date. Caches on success.
 * If offline -> returns cached route + cached clients.
 */
export async function loadRouteForDate(date) {
    let services = [];
    let clients = [];
    let cached = false;
    if (isOnline()) {
        try {
            const [s, c] = await Promise.all([
                api.get("/schedules", { params: { date } }),
                api.get("/clients"),
            ]);
            services = s.data;
            clients = c.data;
            // cache only clients referenced in route
            const refIds = new Set(services.map((x) => x.client_id));
            const refClients = clients.filter((c) => refIds.has(c.id));
            await Promise.all([
                cacheSchedules(services),
                cacheClients(refClients),
            ]);
            await setMeta("last_route_sync", nowIso());
        } catch (e) {
            // network failed despite being marked online -> fallback to cache
            services = await getCachedSchedules({ date });
            clients = await getCachedClients();
            cached = true;
        }
    } else {
        services = await getCachedSchedules({ date });
        clients = await getCachedClients();
        cached = true;
    }
    return { services, clients, cached };
}

export async function loadServiceDetail(id) {
    let svc = null;
    let cached = false;
    if (isOnline()) {
        try {
            const r = await api.get(`/schedules/${id}`);
            svc = r.data;
            await cacheSchedules([svc]);
        } catch (e) {
            svc = await getCachedSchedule(id);
            cached = true;
        }
    } else {
        svc = await getCachedSchedule(id);
        cached = true;
    }
    return { service: svc, cached };
}

export async function startService(scheduleId, payload = {}) {
    if (isOnline()) {
        try {
            const res = await api.post(`/schedules/${scheduleId}/start`, payload || {});
            await cacheSchedules([res.data]);
            return { ok: true, online: true, service: res.data };
        } catch {
            // fall through to offline path
        }
    }
    // Offline / network error: optimistic local update + outbox
    const localPatch = {
        status: "in_progress",
        entry_time: nowIso(),
        _sync_pending: true,
    };
    if (payload && payload.latitude != null && payload.longitude != null) {
        localPatch.start_latitude = payload.latitude;
        localPatch.start_longitude = payload.longitude;
        localPatch.start_accuracy_m = payload.accuracy_m ?? null;
        localPatch.start_location_captured_at = nowIso();
    }
    const local = await updateCachedSchedule(scheduleId, localPatch);
    await enqueueOutbox({
        type: "start_service",
        url: `/schedules/${scheduleId}/start`,
        method: "POST",
        body: payload || {},
        scheduleId,
    });
    return { ok: true, online: false, service: local };
}

export async function markNotDone(scheduleId, reason, extras = {}) {
    const body = { reason, ...extras };
    if (isOnline()) {
        try {
            const res = await api.post(`/schedules/${scheduleId}/not-done`, body);
            await cacheSchedules([res.data]);
            return { ok: true, online: true, service: res.data };
        } catch (e) {}
    }
    const local = await updateCachedSchedule(scheduleId, {
        status: "not_done",
        not_done_reason: reason,
        _sync_pending: true,
    });
    await enqueueOutbox({
        type: "not_done",
        url: `/schedules/${scheduleId}/not-done`,
        method: "POST",
        body,
        scheduleId,
    });
    return { ok: true, online: false, service: local };
}

export async function completeReport(scheduleId, payload) {
    if (isOnline()) {
        try {
            const res = await api.post("/reports/complete", payload);
            await cacheReports([res.data]);
            await updateCachedSchedule(scheduleId, {
                status: "completed",
                exit_time: res.data.exit_time,
                _server_report_id: res.data.id,
            });
            return { ok: true, online: true, report: res.data };
        } catch (e) {
            // fall to offline path
        }
    }
    // Offline path: create local placeholder report + enqueue
    const localId = randId();
    const placeholder = {
        id: localId,
        scheduled_service_id: scheduleId,
        service_date: payload._service_date || new Date().toISOString().slice(0, 10),
        accepted_by_name: payload.accepted_by_name,
        survey: payload.survey,
        products_used: payload.products_used,
        areas_treated: payload.areas_treated,
        recommendations: payload.recommendations,
        problem_areas: payload.problem_areas,
        corrective_actions: payload.corrective_actions,
        follow_ups: payload.follow_ups,
        exterior: payload.exterior,
        interior: payload.interior,
        food_storage: payload.food_storage,
        // Keep photos as in-memory base64 so the local receipt can preview them
        photos: (payload.photos || []).map((p, i) => ({
            id: `local-photo-${i}`,
            file_url: p.base64,           // use data URL directly until sync
            caption: p.caption || null,
            sort_order: i,
        })),
        _photos_count: (payload.photos || []).length,
        exit_time: nowIso(),
        qr_access_pin: randPin(),  // tentative; real one comes from server on sync
        sync_status: "local",
        _local: true,
    };
    await cacheReports([placeholder]);
    await updateCachedSchedule(scheduleId, {
        status: "completed",
        exit_time: placeholder.exit_time,
        _sync_pending: true,
        _local_report_id: localId,
    });
    await enqueueOutbox({
        type: "complete_report",
        url: "/reports/complete",
        method: "POST",
        body: payload,
        scheduleId,
    });
    return { ok: true, online: false, report: placeholder };
}

export async function manualSync() {
    return drain();
}
